import behaviourAutomatedPointsConstants from "@/constants/behaviourAutomatedPointsConstants.js"

const {
  RULE_KEYS,
  RULE_KEY_BY_CATEGORY_ID,
  RULE_KEY_BY_AUTOMATION_TYPE,
  RULE_DEFINITIONS,
  FREQUENCY
} = behaviourAutomatedPointsConstants

/**
 * @returns {Record<string, { enabled: boolean, frequency: string, coreValueId: string|number, pointsAwarded: string|number, settingId: number|null }>}
 */
export function createDefaultAutomatedPointsSettings() {
  return {
    [RULE_KEYS.ON_TIME_FOR_SCHOOL]: {
      enabled: false,
      frequency: FREQUENCY.WEEKLY,
      coreValueId: "",
      pointsAwarded: "",
      settingId: null
    },
    [RULE_KEYS.ON_TIME_FOR_CLASS]: {
      enabled: false,
      frequency: FREQUENCY.DAILY,
      coreValueId: "",
      pointsAwarded: "",
      settingId: null
    }
  }
}

/**
 * @param {Record<string, unknown> | null | undefined} source
 * @returns {ReturnType<typeof createDefaultAutomatedPointsSettings>}
 */
export function cloneAutomatedPointsSettings(source) {
  const base = source || createDefaultAutomatedPointsSettings()
  return JSON.parse(JSON.stringify(base))
}

/**
 * @param {Record<string, unknown>} item
 * @param {ReturnType<typeof createDefaultAutomatedPointsSettings>} defaults
 */
function mapApiSettingToRule(item, defaults) {
  const automationType = String(item?.automation_type ?? "").trim()
  const ruleKey =
    (automationType && RULE_KEY_BY_AUTOMATION_TYPE?.[automationType]) ||
    RULE_KEY_BY_CATEGORY_ID?.[Number(item?.automated_category_id)]
  const fallback =
    defaults[ruleKey] || createDefaultAutomatedPointsSettings()[ruleKey]
  const enabled = Boolean(item?.enabled)
  const pointsRaw = item?.points
  const coreValue = item?.core_value || item?.coreValue || {}
  const coreValueRaw = item?.core_value_id ?? coreValue?.id

  return {
    enabled,
    frequency: String(
      item?.frequency ?? fallback?.frequency ?? FREQUENCY.WEEKLY
    ),
    coreValueId:
      coreValueRaw != null && coreValueRaw !== "" ? String(coreValueRaw) : "",
    coreValueName: String(
      coreValue?.name ?? item?.core_value_name ?? ""
    ).trim(),
    pointsAwarded:
      pointsRaw != null && Number(pointsRaw) > 0 ? String(pointsRaw) : "",
    settingId: item?.id ?? null
  }
}

/**
 * Ensures select options include labels for core values saved on each rule.
 * @param {ReturnType<typeof createDefaultAutomatedPointsSettings>} form
 * @param {Array<{ label: string, value: string }>} options
 * @returns {Array<{ label: string, value: string }>}
 */
export function enrichCoreValueOptionsFromForm(form, options = []) {
  const byValue = new Map(
    (options || []).map((opt) => [
      String(opt?.value ?? ""),
      {
        label: String(opt?.label ?? opt?.value ?? ""),
        value: String(opt.value)
      }
    ])
  )

  ;[RULE_KEYS.ON_TIME_FOR_SCHOOL, RULE_KEYS.ON_TIME_FOR_CLASS].forEach(
    (ruleKey) => {
      const rule = form?.[ruleKey]
      const id =
        rule?.coreValueId != null && String(rule.coreValueId) !== ""
          ? String(rule.coreValueId)
          : ""
      if (!id) return

      const name = String(rule?.coreValueName ?? "").trim()
      if (name) {
        byValue.set(id, { label: name, value: id })
      } else if (!byValue.has(id)) {
        byValue.set(id, { label: id, value: id })
      }
    }
  )

  return Array.from(byValue.values()).sort((a, b) =>
    String(a.label).localeCompare(String(b.label), undefined, {
      sensitivity: "base"
    })
  )
}

/**
 * @param {ReturnType<typeof createDefaultAutomatedPointsSettings>} form
 */
export function normalizeAutomatedPointsFormValues(form) {
  ;[RULE_KEYS.ON_TIME_FOR_SCHOOL, RULE_KEYS.ON_TIME_FOR_CLASS].forEach(
    (ruleKey) => {
      const rule = form?.[ruleKey]
      if (!rule) return
      if (rule.coreValueId != null && String(rule.coreValueId) !== "") {
        rule.coreValueId = String(rule.coreValueId)
      }
      const points = rule.pointsAwarded
      if (points != null && String(points).trim() !== "") {
        rule.pointsAwarded = String(points)
      }
    }
  )
  return form
}

/**
 * @param {Array<Record<string, unknown>>} settings
 * @returns {ReturnType<typeof createDefaultAutomatedPointsSettings>}
 */
export function mapApiAutomatedPointsToForm(settings) {
  const form = createDefaultAutomatedPointsSettings()
  const list = Array.isArray(settings) ? settings : []

  list.forEach((item) => {
    const automationType = String(item?.automation_type ?? "").trim()
    const ruleKey =
      (automationType && RULE_KEY_BY_AUTOMATION_TYPE?.[automationType]) ||
      RULE_KEY_BY_CATEGORY_ID?.[Number(item?.automated_category_id)]
    if (!ruleKey) return
    form[ruleKey] = mapApiSettingToRule(item, form)
  })

  return form
}

/**
 * @param {Record<string, unknown>} form
 * @returns {{ settings: Array<Record<string, unknown>> }}
 */
export function buildAutomatedPointsUpsertPayload(form) {
  const settings = (RULE_DEFINITIONS || []).map((def) => {
    const ruleKey = def?.ruleKey
    const rule = (ruleKey && form?.[ruleKey]) || {}
    const enabled = Boolean(rule?.enabled)
    const frequency = String(rule?.frequency ?? FREQUENCY.WEEKLY)
    const coreValueRaw = rule?.coreValueId
    const coreValueId =
      coreValueRaw != null && String(coreValueRaw) !== ""
        ? Number(coreValueRaw)
        : null
    const pointsRaw = Number(rule?.pointsAwarded)
    const automationType = String(def?.automationType ?? "").trim()

    if (!enabled) {
      return {
        automation_type: automationType,
        enabled: false,
        frequency,
        core_value_id: coreValueId,
        points: Number.isFinite(pointsRaw) && pointsRaw >= 0 ? pointsRaw : 0
      }
    }

    return {
      automation_type: automationType,
      enabled: true,
      frequency,
      core_value_id: coreValueId,
      points: Number.isFinite(pointsRaw) && pointsRaw >= 1 ? pointsRaw : 0
    }
  })

  return { settings }
}

/**
 * Validates only enabled rules.
 * @param {Record<string, unknown>} form
 * @returns {{ valid: boolean, errors: Record<string, Record<string, string>> }}
 */
export function validateAutomatedPointsForm(form) {
  const errors = {}
  let valid = true

  ;[RULE_KEYS.ON_TIME_FOR_SCHOOL, RULE_KEYS.ON_TIME_FOR_CLASS].forEach(
    (ruleKey) => {
      const rule = form?.[ruleKey]
      if (!rule?.enabled) return

      const ruleErrors = {}
      if (!String(rule?.frequency ?? "").trim()) {
        ruleErrors.frequency = "required"
      }
      if (rule?.coreValueId == null || String(rule.coreValueId).trim() === "") {
        ruleErrors.coreValueId = "required"
      }
      const points = Number(rule?.pointsAwarded)
      if (!Number.isFinite(points) || points < 1) {
        ruleErrors.pointsAwarded = "required"
      }

      if (Object.keys(ruleErrors).length) {
        errors[ruleKey] = ruleErrors
        valid = false
      }
    }
  )

  return { valid, errors }
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function extractFirstValidationMessage(value) {
  if (Array.isArray(value)) {
    return String(value[0] ?? "").trim()
  }
  return String(value ?? "").trim()
}

const AUTOMATED_POINTS_API_FIELD_TO_FORM_FIELD = Object.freeze({
  frequency: "frequency",
  core_value_id: "coreValueId",
  points: "pointsAwarded"
})

/**
 * Maps API keys like `settings.0.points` to form rule field errors.
 * @param {Record<string, unknown> | null | undefined} errors
 * @returns {Record<string, Record<string, string>>}
 */
export function mapAutomatedPointsApiFieldErrors(errors) {
  const out = {}

  if (!errors || typeof errors !== "object") {
    return out
  }

  Object.entries(errors).forEach(([key, messages]) => {
    const match = String(key ?? "").match(/^settings\.(\d+)\.(\w+)$/)
    if (!match) return

    const index = Number(match[1])
    const apiField = match[2]
    const formField = AUTOMATED_POINTS_API_FIELD_TO_FORM_FIELD[apiField]
    const ruleDef = RULE_DEFINITIONS?.[index]
    const ruleKey = ruleDef?.ruleKey

    if (!ruleKey || !formField) return

    const message = extractFirstValidationMessage(messages)
    if (!message) return

    if (!out[ruleKey]) {
      out[ruleKey] = {}
    }
    out[ruleKey][formField] = message
  })

  return out
}

/**
 * @param {Record<string, Record<string, string>> | null | undefined} fieldErrors
 * @returns {string[]}
 */
function collectAutomatedPointsApiFieldMessages(fieldErrors) {
  return Object.values(fieldErrors || {}).flatMap((ruleErrors) =>
    Object.values(ruleErrors || {})
      .map((message) => String(message ?? "").trim())
      .filter(Boolean)
  )
}

/**
 * @param {unknown} err
 * @returns {string}
 */
export function parseAutomatedPointsApiError(err) {
  const data = err?.response?.data
  const fieldErrors = mapAutomatedPointsApiFieldErrors(data?.errors)
  const validationMessages = collectAutomatedPointsApiFieldMessages(fieldErrors)

  if (validationMessages.length) {
    return validationMessages[0]
  }

  return String(data?.error ?? data?.message ?? err?.message ?? "").trim() || ""
}
